import React, { useState, useEffect } from "react";
import api from "../../api";
import "../../styles/GameUIStyles.css";
import DOMPurify from "dompurify";
import {
  Col,
  Row,
  Container,
} from "reactstrap";
import { FaPaperPlane, FaClock, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { useLocation } from "react-router-dom";

const SendMessage = () => {
  document.title = "Send Message | Pokémon Radiance";

  const location = useLocation();
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [message, setMessage] = useState({ text: "", type: "" });
  const [cooldown, setCooldown] = useState(0);

  // On component mount, check for query parameter "to" (or you could use "user" if needed)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const recipient = params.get("to");
    if (recipient) {
      setTo(recipient);
    }
  }, [location.search]);

  // Validate and sanitize inputs
  const validateInputs = () => {
    // Check for empty fields
    if (!to.trim()) {
      setMessage({ text: "Please enter a recipient username", type: "error" });
      return false;
    }
    
    if (!subject.trim()) {
      setMessage({ text: "Please enter a subject", type: "error" });
      return false;
    }
    
    if (!content.trim()) {
      setMessage({ text: "Please enter a message", type: "error" });
      return false;
    }
    
    // Check length limits
    if (subject.length > 50) {
      setMessage({ text: "Subject must be 50 characters or less", type: "error" });
      return false;
    }
    
    if (content.length > 2000) {
      setMessage({ text: "Message must be 2000 characters or less", type: "error" });
      return false;
    }
    
    return true;
  };

  const sendMessage = async () => {
    if (cooldown > 0) {
      setMessage({
        text: `Please wait ${cooldown} seconds before sending another message.`,
        type: "error",
      });
      return;
    }
    
    // Validate inputs first
    if (!validateInputs()) {
      return;
    }

    try {
      // Sanitize inputs before sending
      const sanitizedTo = DOMPurify.sanitize(to.trim());
      const sanitizedSubject = DOMPurify.sanitize(subject.trim());
      const sanitizedContent = DOMPurify.sanitize(content.trim());
      
      const response = await api.post(
        "/v1/messages/send",
        { 
          recipient: sanitizedTo, 
          subject: sanitizedSubject, 
          content: sanitizedContent 
        },
        { withCredentials: true },
      );

      setMessage({ text: response.data.message, type: "success" });
      setTo("");
      setSubject("");
      setContent("");

      // Start cooldown (60 seconds)
      setCooldown(60);
      const interval = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      setMessage({
        text: error.response?.detail || "Failed to send message.",
        type: "error",
      });
    }
  };

  return (
    <React.Fragment>
      <div className="game-page-content">
        <Container fluid>
          <div className="game-breadcrumb">
            <div className="game-breadcrumb-item">Messages</div>
            <div className="game-breadcrumb-item">Send Message</div>
          </div>

          {message.text && (
            <div className={`game-alert game-alert-${message.type === "success" ? "success" : "error"}`}>
              <div className="game-alert-icon">
                {message.type === "success" ? <FaCheckCircle /> : <FaExclamationCircle />}
              </div>
              <div className="game-alert-text">{message.text}</div>
            </div>
          )}

          <Row className="mb-4">
            <Col xl={12}>
              <div className="game-card">
                <div className="game-card-header">
                  <h4 className="game-card-title">Compose Message</h4>
                </div>
                <div className="game-card-body">
                  <div className="game-input-group">
                    <label className="game-input-label">Recipient</label>
                    <input
                      type="text"
                      className="game-input"
                      placeholder="Enter recipient username"
                      value={to}
                      onChange={(e) => {
                        // Limit length and prevent special characters in username
                        const value = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                        setTo(value);
                      }}
                      maxLength={20}
                    />
                  </div>
                  <div className="game-input-group">
                    <label className="game-input-label">Subject</label>
                    <input
                      type="text"
                      className="game-input"
                      placeholder="Enter subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      maxLength={50}
                    />
                    <div className="text-secondary mt-1" style={{fontSize: '0.8rem', textAlign: 'right'}}>
                      {subject.length}/50 characters
                    </div>
                  </div>
                  <div className="game-input-group">
                    <label className="game-input-label">Message</label>
                    <textarea
                      className="game-input"
                      style={{
                        minHeight: '200px',
                        resize: 'vertical',
                        padding: '12px',
                        lineHeight: '1.5',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                      }}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Start typing your message here..."
                      maxLength={2000}
                    />
                    <div className="text-secondary mt-1" style={{fontSize: '0.8rem', textAlign: 'right'}}>
                      {content.length} characters
                    </div>
                  </div>
                  <div className="d-flex justify-content-end mt-4">
                    <button
                      className="game-button"
                      onClick={sendMessage}
                      disabled={cooldown > 0}
                    >
                      <span>Send Message</span>
                      <FaPaperPlane className="game-button-icon" />
                    </button>
                  </div>
                  {cooldown > 0 && (
                    <div className="d-flex align-items-center mt-2 text-danger" style={{fontSize: '0.9rem'}}>
                      <FaClock className="anim-pulse" style={{marginRight: '8px'}} />
                      <span>You can send another message in {cooldown} seconds</span>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SendMessage;
